import { AnySelector } from './selectorTypes';
import { Settings } from './settings';

export type TypeOfNote = 'takeaway' | 'source' | 'annote';
export const TYPES_OF_NOTES: TypeOfNote[] = ['takeaway', 'source', 'annote'];
export const NOTE_TAB_LABELS = { all: 'All', takeaway: 'Notes', source: 'Articles', annote: 'Highlights' };
export type UUID = string;

export type CardSize = 'SMALL' | 'MEDIUM' | 'LARGE';

export type Generator = 'human' | 'ai';

export type SharedDetails = {
  generator?: Generator;
  tool?: string;
};

export type SourceDetails = SharedDetails & {
  textContent: string;
  length: number;
  summary: string;
  byline: string;
  dir: string;
  siteName: string;
  lang: string;
  author: string | null;
  publishedTime: string | null;
  canonicalUrl?: string;
  faviconUrl?: string; // Todo: run a batch process and make this not optional
  pageCount?: number; // Number of pages (for PDF sources)
};

export type AnnoteDetails = SharedDetails & {
  selectors: AnySelector[];
};

export type TakeawayDetails = SharedDetails & {
  // sourceId: UUID;
};

export type NoteDetails = AnnoteDetails | SourceDetails | TakeawayDetails;

export type Position = { x: number; y: number };

// a briefer version of a note to lower token usage on AI tools
export type SmallNote = {
  id: UUID;
  title: string;
  type: TypeOfNote;
};

// ! When this is updated make sure to update ensureOnlyNoteFields in utils/notes.ts
export type Note = {
  id: UUID;
  userId?: UUID; // userId should be auto set by the server
  type: TypeOfNote; // one of 'annote', 'source', 'takeaway'
  title: string;
  value: string;
  url?: string; // url if a source
  createdFromId?: UUID; // id of the note that this note was created from if it was created from another note
  details?: NoteDetails; // json object with the details of the note specific to the type of note
  createdAt: string;
  syncedAt?: string | null;
  deletedAt?: string | null; // null if not yet deleted
  links: UUID[]; // denormalized list of ids of notes that are linked to this note
  backlinks: UUID[]; // denormalized list of ids of notes that have this note as a backlink
  suggestionId?: UUID; // CACHE-ONLY: AI suggestion (create if !syncedAt, delete if syncedAt)
  // These take a lot of space and are not used in the client
  // embedding: number[]; // vector embedding of the note
  // fts: string; // full text search vector of the note
};

export type Profile = {
  userId: UUID;
  username: string;
  displayName: string;
  email: string;
  photoUrl?: string;
  createdAt: string;
  syncedAt: string | null;
  deletedAt?: string | null;
  blockedHosts: string[];
  isStaff: boolean;
  settings?: Settings;
};

export type NoteMap = Record<UUID, Note>;

export type Annote = Note & {
  type: 'annote';
  createdFromId: string;
  details: AnnoteDetails;
};

export type Source = Note & {
  type: 'source';
  url: string;
  details: SourceDetails;
};

export type Takeaway = Note & {
  type: 'takeaway';
};

// the result of match_document_sections we want from querying the document_sections table
export type DocumentSection = {
  documentId: UUID;
  index: number;
  content: string;
  similarity: number;
};

export type RelatedResult = {
  searchedNoteId: UUID;
  relatedNoteId: UUID;
  similarity: number;
  title: string;
};

export type Database = {
  Profile: Profile;
  Notes: Note;
};

export type LinkKind = 'tree' | 'graph';

export type LinkInfo = {
  fromNote: Note;
  toNote: Note;
  kind: LinkKind;
};

export interface AnnoteIndexEntry {
  links: UUID[]; // Notes this annote links to
  backlinks: UUID[]; // Notes that link to this annote
}

// Re-export suggestion types from suggestions.ts
export * from './suggestions';

// Export settings types
export * from './settings';

export const isAnnote = (note: Note): note is Annote => {
  return note?.type === 'annote';
};

export const isSource = (note: Note): note is Source => {
  return note?.type === 'source';
};

export const isTakeaway = (note: Note): note is Takeaway => {
  return note?.type === 'takeaway';
};

export const isNote = (note: Note): note is Note => {
  return note?.type === 'annote' || note?.type === 'source' || note?.type === 'takeaway';
};

export const isUUID = (id: string): id is UUID => {
  // UUID format: 8-4-4-4-12 hexadecimal characters
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return id != null && id !== '' && uuidRegex.test(id);
};
